import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Loader2, NotebookPen, ShieldAlert, Scale, Zap, BookMarked, Users, Sparkles, Filter } from "lucide-react";
import type { Stakeholder } from "@/lib/projectStore";
import { format, parseISO } from "date-fns";

const BASE = process.env.REACT_APP_BACKEND_URL;

interface Props {
  baProjectId: string | null;
  stakeholders?: Stakeholder[];
}

type FeedType = "meeting" | "decision" | "risk" | "evolution";

interface FeedItem {
  id: string;
  date: string;
  type: FeedType;
  title: string;
  summary: string;
  people: string[];
  meta?: string;
}

const TYPE_CONFIG: Record<FeedType, { label: string; icon: React.ComponentType<{ className?: string }>; bg: string; text: string; border: string }> = {
  meeting:   { label: "Meeting",   icon: NotebookPen, bg: "bg-blue-500/10",   text: "text-blue-600 dark:text-blue-400",   border: "border-blue-300/40" },
  decision:  { label: "Decision",  icon: Scale,       bg: "bg-violet-500/10", text: "text-violet-600 dark:text-violet-400", border: "border-violet-300/40" },
  risk:      { label: "Risk",      icon: ShieldAlert, bg: "bg-red-500/10",    text: "text-red-600 dark:text-red-400",     border: "border-red-300/40" },
  evolution: { label: "Change",    icon: BookMarked,  bg: "bg-amber-500/10",  text: "text-amber-600 dark:text-amber-400", border: "border-amber-300/40" },
};

export default function CatchMeUpPanel({ baProjectId, stakeholders = [] }: Props) {
  const { token } = useAuth();
  const authHeader = token ? { Authorization: `Bearer ${token}` } : {};

  const [feed, setFeed] = useState<FeedItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [briefing, setBriefing] = useState("");
  const [briefLoading, setBriefLoading] = useState(false);
  const [activeFilters, setActiveFilters] = useState<Set<FeedType>>(new Set<FeedType>(["meeting", "decision", "risk", "evolution"]));

  const resolveAttendees = (raw: string): string[] => {
    if (!raw) return [];
    return raw.split(",").map(p => {
      const s = stakeholders.find(x => x.id === p.trim());
      return s ? s.name : p.trim();
    }).filter(Boolean);
  };

  useEffect(() => {
    if (baProjectId) loadFeed();
    else setFeed([]);
  }, [baProjectId]);

  const loadFeed = async () => {
    if (!baProjectId) return;
    setLoading(true);
    try {
      const [meetingsRes, decisionsRes, risksRes, evolutionRes] = await Promise.allSettled([
        axios.get(`${BASE}/api/tools/meeting-notes?project_id=${baProjectId}`, { headers: authHeader }),
        axios.get(`${BASE}/api/tools/decisions?project_id=${baProjectId}`, { headers: authHeader }),
        axios.get(`${BASE}/api/tools/risks?project_id=${baProjectId}`, { headers: authHeader }),
        axios.get(`${BASE}/api/tools/evolution-logs?project_id=${baProjectId}`, { headers: authHeader }),
      ]);

      const items: FeedItem[] = [];

      if (meetingsRes.status === "fulfilled") {
        (meetingsRes.value.data || []).forEach((m: any) => {
          items.push({
            id: `meeting-${m.id}`,
            date: m.meeting_date || m.created_at,
            type: "meeting",
            title: m.title,
            summary: m.raw_notes?.slice(0, 200) || "",
            people: resolveAttendees(m.attendees || ""),
            meta: m.extracted_items?.length ? `${m.extracted_items.length} items extracted` : undefined,
          });
        });
      }

      if (decisionsRes.status === "fulfilled") {
        (decisionsRes.value.data || []).forEach((d: any) => {
          items.push({
            id: `decision-${d.id}`,
            date: d.decision_date || d.created_at,
            type: "decision",
            title: d.title,
            summary: d.rationale || d.description || "",
            people: d.owner ? [d.owner] : [],
            meta: d.impact_level ? `Impact: ${d.impact_level}` : undefined,
          });
        });
      }

      if (risksRes.status === "fulfilled") {
        (risksRes.value.data || []).forEach((r: any) => {
          items.push({
            id: `risk-${r.id}`,
            date: r.identified_date || r.created_at,
            type: "risk",
            title: r.title,
            summary: r.mitigation || r.description || "",
            people: r.owner ? [r.owner] : [],
            meta: r.likelihood && r.impact ? `${r.likelihood} likelihood · ${r.impact} impact` : undefined,
          });
        });
      }

      if (evolutionRes.status === "fulfilled") {
        (evolutionRes.value.data || []).forEach((e: any) => {
          items.push({
            id: `evolution-${e.id}`,
            date: e.created_at,
            type: "evolution",
            title: e.title || e.change_type || "Change logged",
            summary: e.description || e.summary || "",
            people: e.author ? [e.author] : [],
          });
        });
      }

      // sort newest first
      items.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
      setFeed(items);
    } finally {
      setLoading(false);
    }
  };

  const filtered = useMemo(
    () => feed.filter(item => activeFilters.has(item.type)),
    [feed, activeFilters]
  );

  const toggleFilter = (type: FeedType) => {
    setActiveFilters(prev => {
      const next = new Set(prev);
      if (next.has(type)) { if (next.size > 1) next.delete(type); }
      else next.add(type);
      return next;
    });
  };

  const generateBriefing = async () => {
    if (!feed.length) return;
    setBriefLoading(true);
    setBriefing("");
    try {
      const context = feed.slice(0, 40).map(item =>
        `[${item.type.toUpperCase()} | ${item.date.slice(0, 10)}] ${item.title}: ${item.summary.slice(0, 150)}`
      ).join("\n");

      const res = await axios.post(
        `${BASE}/api/tools/brain-trajectory`,
        {
          entries: feed.slice(0, 40).map(item => ({
            source: item.type,
            timestamp: item.date,
            content: `${item.title}: ${item.summary}`,
          }))
        },
        { headers: authHeader }
      );

      // Use the themes to build a briefing paragraph
      const themes = (res.data?.themes || []).map((t: any) => t.name).join(", ");
      setBriefing(
        themes
          ? `This project has been focused on: ${themes}. Review the timeline below for full context — start with Decisions to understand why key choices were made, then Meetings to see who was involved.`
          : "Could not generate briefing — add more project data first."
      );
    } catch {
      // Fallback: build a simple briefing from the data without AI
      const meetings = feed.filter(f => f.type === "meeting").length;
      const decisions = feed.filter(f => f.type === "decision").length;
      const risks = feed.filter(f => f.type === "risk").length;
      const people = Array.from(new Set(feed.flatMap(f => f.people))).filter(Boolean).slice(0, 6);
      setBriefing(
        `Project has ${meetings} meeting${meetings !== 1 ? "s" : ""}, ${decisions} decision${decisions !== 1 ? "s" : ""}, and ${risks} logged risk${risks !== 1 ? "s" : ""}. ` +
        (people.length ? `Key people: ${people.join(", ")}. ` : "") +
        `Start with Decisions to understand the why, then Meeting Notes for full context.`
      );
    } finally {
      setBriefLoading(false);
    }
  };

  const formatDate = (d: string) => {
    try { return format(parseISO(d), "d MMM yyyy"); } catch { return d?.slice(0, 10) || ""; }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="shrink-0 border-b border-border px-5 py-3 flex items-center justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold text-foreground">Catch Me Up</h2>
          <p className="text-[11px] text-muted-foreground">
            Everything that happened on this project — for new joiners or returning team members.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {feed.length > 0 && (
            <Button size="sm" variant="outline" className="h-7 gap-1 text-[11px]" onClick={generateBriefing} disabled={briefLoading}>
              {briefLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />}
              Brief me
            </Button>
          )}
          <Button size="sm" variant="ghost" className="h-7 text-[11px]" onClick={loadFeed} disabled={loading}>
            {loading ? <Loader2 className="h-3 w-3 animate-spin" /> : "Refresh"}
          </Button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4" style={{ scrollbarWidth: "thin" }}>
        {/* AI Briefing */}
        {briefing && (
          <div className="p-4 rounded-lg border border-primary/30 bg-primary/5 text-sm text-foreground leading-relaxed">
            <p className="text-[10px] font-mono uppercase tracking-wider text-primary mb-2 flex items-center gap-1">
              <Sparkles className="h-3 w-3" /> AI onboarding brief
            </p>
            {briefing}
          </div>
        )}

        {/* Filter chips */}
        {feed.length > 0 && (
          <div className="flex items-center gap-2 flex-wrap">
            <Filter className="h-3 w-3 text-muted-foreground shrink-0" />
            {(Object.keys(TYPE_CONFIG) as FeedType[]).map(type => {
              const cfg = TYPE_CONFIG[type];
              const count = feed.filter(f => f.type === type).length;
              if (count === 0) return null;
              const active = activeFilters.has(type);
              return (
                <button
                  key={type}
                  onClick={() => toggleFilter(type)}
                  className={`text-[11px] px-2.5 py-0.5 rounded-full border transition-colors flex items-center gap-1 ${
                    active ? `${cfg.bg} ${cfg.text} ${cfg.border}` : "border-border text-muted-foreground hover:border-muted-foreground/40"
                  }`}
                >
                  {cfg.label} <span className="opacity-60">{count}</span>
                </button>
              );
            })}
          </div>
        )}

        {/* Empty */}
        {!loading && feed.length === 0 && (
          <div className="text-center py-16 text-muted-foreground">
            <BookMarked className="h-8 w-8 mx-auto mb-3 opacity-30" />
            <p className="text-sm font-medium">Nothing logged yet</p>
            <p className="text-[11px] mt-1">Add meetings, decisions and risks — they'll appear here in order.</p>
          </div>
        )}

        {loading && (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        )}

        {/* Feed */}
        <div className="relative">
          {/* Spine */}
          {filtered.length > 1 && (
            <div className="absolute left-[19px] top-6 bottom-6 w-px bg-border" />
          )}

          <div className="space-y-3">
            {filtered.map(item => {
              const cfg = TYPE_CONFIG[item.type];
              const Icon = cfg.icon;
              return (
                <div key={item.id} className="flex gap-3">
                  {/* Node */}
                  <div className={`shrink-0 h-10 w-10 rounded-full border flex items-center justify-center z-10 ${cfg.bg} ${cfg.border}`}>
                    <Icon className={`h-4 w-4 ${cfg.text}`} />
                  </div>

                  {/* Card */}
                  <div className="flex-1 min-w-0 rounded-lg border border-border bg-card px-3 py-2.5 space-y-1">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`text-[10px] font-bold uppercase tracking-wider ${cfg.text}`}>{cfg.label}</span>
                        {item.meta && (
                          <span className="text-[10px] text-muted-foreground">{item.meta}</span>
                        )}
                      </div>
                      <span className="text-[10px] text-muted-foreground shrink-0">{formatDate(item.date)}</span>
                    </div>
                    <p className="text-[12px] font-semibold text-foreground leading-snug">{item.title}</p>
                    {item.summary && (
                      <p className="text-[11px] text-muted-foreground leading-snug line-clamp-2">{item.summary}</p>
                    )}
                    {item.people.length > 0 && (
                      <div className="flex items-center gap-1 pt-0.5 flex-wrap">
                        <Users className="h-2.5 w-2.5 text-muted-foreground shrink-0" />
                        {item.people.map(p => (
                          <span key={p} className="text-[10px] px-1.5 py-0 rounded-full bg-muted text-muted-foreground border border-border">
                            {p}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
